import React, { memo, useCallback, useEffect, useRef, useState } from 'react'
import { Alert, Form, message, Modal, Skeleton } from 'antd'
import { useSelector } from 'react-redux'
import {
  getCardCurrentStage,
  getCardData,
  getCardError,
  getCardStagesHistory,
  getCardStagesItems,
  getDataLoading,
  getVisibleFormData,
} from '../../selectors'
import { useDebounce } from 'src/react-app/hooks/useDebounce/useDebounce'
import { HStack, VStack } from '../../../../../ui/Stack'
import { DefaultJsonForm } from '../../../../../components/v2/default-json-form'
import { useSaveResult } from '../../hooks/useSaveResult'
import { useNavigate, useParams } from 'react-router-dom'
import { isFormDataRequired } from '../../../../../../lib/utils/collections'
import { updateFormData } from '../../../../../store/claim_request/action-creators'
import { clearClaimRequestData } from '../../../../../store/claim_request/actions'
import { useAppDispatch } from '../../../../../hooks/useAppDispatch/useAppDispatch'

const formItemLayout = {
  labelCol: {
    xs: { span: 24 },
    sm: { span: 8 },
  },
  wrapperCol: {
    xs: { span: 24 },
    sm: { span: 24 },
  },
}

export const DataForm: React.FC = memo(() => {
  const { id } = useParams<{ id: string }>()
  if (!id) return null

  const [form] = Form.useForm()
  const formData = useSelector(getCardData)
  const formIsLoading = useSelector(getDataLoading)
  const formError = useSelector(getCardError)
  const visibleFormData = useSelector(getVisibleFormData)
  const formRef = useRef<typeof visibleFormData>()
  const { saveResult } = useSaveResult()
  const navigate = useNavigate()

  const stages = useSelector(getCardStagesItems)
  const current_stage = useSelector(getCardCurrentStage)
  const stages_history = useSelector(getCardStagesHistory)
  const dispatch = useAppDispatch()
  const [prevFormData, setPrevFormData] = useState<any>(undefined)

  const hasValidValues = (obj: Record<string, any>, keys: string[]): boolean => {
    return keys.some(
      key => obj[key] !== null && obj[key] !== undefined && obj[key] !== false,
    )
  }

  const saveFormData = useCallback(async () => {
    const schema = visibleFormData?.resultSchema
    const form = formRef?.current?.resultObject

    const isRequiredFields = isFormDataRequired(form, schema)
    if (id === 'new' && !isRequiredFields) {
      return
    }

    if (form && schema && form && hasValidValues(form, ['stage', 'classification_id', 'dealer_requirement_id', 'claim', 'dealer_event', 'files'])) {
      await saveResult(id, form)
        .then(() => {
          message.success('Данные успешно сохранены')
        })
    }
  }, [id, visibleFormData, history, formRef])

  useEffect(() => {
    const redirectUrl = formData?.redirectUrl
    if (redirectUrl) {
      navigate(redirectUrl)
    }
  }, [formData])

  useEffect(() => {
    if (visibleFormData) {
      formRef.current = visibleFormData
    }
  }, [visibleFormData])

  const debounceSaveCard = useDebounce(
    () => {
      saveFormData()
    }, 1000,
  )

  const onChangeForm = useCallback(async ({ formData }) => {
    if (!formRef.current) {
      return
    }
    try {
      await debounceSaveCard()
    } finally {
      setPrevFormData({ ...formData })
      formRef.current.resultObject = formData
    }
  }, [formRef, debounceSaveCard, prevFormData])

  useEffect(() => {
    return () => {
      // очищает промежуточное состояние
      setPrevFormData(undefined)
      // стор не очищаем здесь, чтобы не терять данные при переключении вкладок
    }
  }, [])

  if (formError && !formIsLoading && !formData) {
    return (
      <HStack gap={'8'} className={'mapWrapper'}>
        При выполнении запроса возникла ошибка.
        Попробуйте перезагрузить страницу или открыть карточку повторно.
      </HStack>
    )
  }

  const toClaim = useCallback(() => {
    if (!id) return
    const data = formRef?.current
    if (data) {
      const isRequiredFields = isFormDataRequired(data.resultObject, data.resultSchema)
      if (!isRequiredFields) {
        Modal.warning({
          title: 'Предупреждение',
          content: 'Заполните все обязательные поля',
        })
        return
      } else {
        dispatch(updateFormData(id, { stage: 'approved' }))
      }
    }
  }, [formRef, id])

  return (
    <>
      {formError ? (
        <div className={'alertWrapper'}>
          <Alert
            message="При выполнении операции возникла ошибка:"
            showIcon
            type="error"
            description={formError}
          />
        </div>
      ) : null}
      <Form
        {...formItemLayout}
        layout="horizontal"
        form={form}
        scrollToFirstError={true}
        style={{ width: '100%' }}
      >
        {!formIsLoading ? (
          <VStack gap={'16'} max align={'stretch'} className={'appealsForm'}>
            {visibleFormData && (
              <DefaultJsonForm
                formData={visibleFormData.resultObject}
                schema={visibleFormData.resultSchema}
                onChange={onChangeForm}
                orientation="horizontal"
              />
            )}
          </VStack>
        ) : (
          <Skeleton active />
        )}
      </Form>
    </>
  )
})
